import { Plus, ImageIcon } from 'lucide-react';
import { Button } from '../../../components/ui/button';

export default function ProductGrid({ items, loading, viewMode, onAddItem }) {
  if (loading) {
    return (
      <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'} gap-3`}>
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
            <div className="w-full aspect-square bg-slate-200 rounded-lg mb-3" />
            <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
            <div className="h-3 bg-slate-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-16">
        <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">No items found</p>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="space-y-2">
        {items.map(item => (
          <div
            key={item.id}
            className="bg-white rounded-lg p-3 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onAddItem(item)}
            data-testid={`pos-item-${item.id}`}
          >
            {item.image_url ? (
              <img
                src={item.image_url}
                alt={item.name}
                className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <ImageIcon className="w-6 h-6 text-slate-400" />
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-slate-900 truncate">{item.name}</h3>
              <p className="text-sm text-slate-500 truncate">{item.category}</p>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="font-bold text-orange-500 text-lg">
                £{item.price.toFixed(2)}
              </span>
              <Button
                size="icon"
                className="bg-orange-500 hover:bg-orange-600 rounded-full w-8 h-8"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddItem(item);
                }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Grid view
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {items.map(item => (
        <div
          key={item.id}
          className="bg-white rounded-xl p-3 shadow-sm hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => onAddItem(item)}
          data-testid={`pos-item-${item.id}`}
        >
          {/* Image */}
          <div className="relative aspect-square mb-3 rounded-lg overflow-hidden bg-slate-100">
            {item.image_url ? (
              <img
                src={item.image_url}
                alt={item.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="w-10 h-10 text-slate-300" />
              </div>
            )}
            
            {/* Quick Add Button */}
            <Button
              size="icon"
              className="absolute bottom-2 right-2 bg-orange-500 hover:bg-orange-600 rounded-full w-8 h-8 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
              onClick={(e) => {
                e.stopPropagation();
                onAddItem(item);
              }}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Info */}
          <div>
            <h3 className="font-medium text-slate-900 text-sm line-clamp-1">{item.name}</h3>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-slate-500">{item.category}</span>
              <span className="font-bold text-orange-500">£{item.price.toFixed(2)}</span>
            </div>
          </div>

          {/* Allergens/Dietary Info */}
          {(item.allergens?.length > 0 || item.dietary_info?.length > 0) && (
            <div className="mt-2 flex flex-wrap gap-1">
              {item.dietary_info?.map((info, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                  {info}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
